import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IStoreState } from "../redux/initialStoreState";
import {
  fetchReconciliationStatusApi,
  reconciliationPollingStatusUpdated,
  reconciliationPollingFailure,
  reconciliationPollingFailureReset,
  reconciliationPollingReset,
  fetchReconciliationDetailsSummaryAsync,
  reconciliationPollingProgressUpdated,
} from "../redux/aiReconciliation/aiReconciliationActions";
import { showMessage } from "../redux/messages/messagesActions";
import { ReconciliationJobStatus } from "../redux/aiReconciliation/aiReconciliation.types";

const POLLING_INTERVAL_MS = 10000;
const TIMEOUT_MS = 300000; // 3 minutes
const MAX_CONSECUTIVE_FAILURES = 3;

export const useReconciliationPolling = (
  templateCode: string | null,
  documentCode: string | null
) => {
  const dispatch = useDispatch();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const { processLogCode, pollingStatus, consecutiveFailures, pollingStartedAt, progress } =
    useSelector((state: IStoreState) => state.aiReconciliation.polling);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleCompleted = useCallback(() => {
    stopPolling();
    dispatch(reconciliationPollingStatusUpdated("COMPLETED"));

    if (templateCode && documentCode) {
      dispatch(fetchReconciliationDetailsSummaryAsync(templateCode, documentCode) as any);
    }

    dispatch(
      showMessage({
        type: "success",
        message: "Reconciliation completed successfully.",
        displayAs: "snackbar",
      })
    );
  }, [dispatch, stopPolling, templateCode, documentCode]);

  const handleDeadLetter = useCallback(
    (errorMessage: string | null) => {
      stopPolling();
      const truncated =
        errorMessage && errorMessage.length > 500
          ? errorMessage.substring(0, 500)
          : errorMessage;
      const displayMessage =
        truncated || "Reconciliation failed due to an unexpected error.";

      dispatch(reconciliationPollingStatusUpdated("DEAD_LETTER", displayMessage));
      dispatch(
        showMessage({
          type: "error",
          message: displayMessage,
          displayAs: "snackbar",
        })
      );
    },
    [dispatch, stopPolling]
  );

  const handleTimeout = useCallback(() => {
    stopPolling();
    dispatch(reconciliationPollingStatusUpdated("TIMED_OUT"));
    dispatch(
      showMessage({
        type: "warning",
        message:
          "Reconciliation is still processing on the server. You may check back or retry later.",
        displayAs: "snackbar",
      })
    );
  }, [dispatch, stopPolling]);

  const handleNetworkError = useCallback(() => {
    stopPolling();
    dispatch(reconciliationPollingStatusUpdated("NETWORK_ERROR"));
    dispatch(
      showMessage({
        type: "error",
        message:
          "Unable to reach the server. Please check your connection and retry.",
        displayAs: "snackbar",
      })
    );
  }, [dispatch, stopPolling]);

  const poll = useCallback(async () => {
    if (!processLogCode || !isMountedRef.current) return;

    // Check timeout
    if (pollingStartedAt && Date.now() - pollingStartedAt >= TIMEOUT_MS) {
      handleTimeout();
      return;
    }

    try {
      const res = await fetchReconciliationStatusApi(processLogCode);
      const {
        status,
        error_message,
        total_records,
        processed_records,
        matched_records,
        unmatched_records,
        records_with_differences,
        progress_percent,
        progress_stage,
      } = res.data.data;

      if (!isMountedRef.current) return;

      // Reset failure counter on success
      if (consecutiveFailures > 0) {
        dispatch(reconciliationPollingFailureReset());
      }

      // Dispatch progress update
      dispatch(
        reconciliationPollingProgressUpdated({
          totalRecords: total_records ?? 0,
          processedRecords: processed_records ?? 0,
          matchedRecords: matched_records ?? 0,
          unmatchedRecords: unmatched_records ?? 0,
          recordsWithDifferences: records_with_differences ?? 0,
          progressPercent: progress_percent ?? 0,
          progressStage: progress_stage ?? null,
        })
      );

      switch (status) {
        case "COMPLETED":
          handleCompleted();
          break;
        case "DEAD_LETTER":
          handleDeadLetter(error_message);
          break;
        case "QUEUED":
        case "PROCESSING":
        case "FAILED":
          dispatch(reconciliationPollingStatusUpdated(status));
          break;
        default:
          dispatch(
            reconciliationPollingStatusUpdated(status as ReconciliationJobStatus)
          );
      }
    } catch (err: any) {
      if (!isMountedRef.current) return;

      dispatch(reconciliationPollingFailure());
      const newFailureCount = consecutiveFailures + 1;

      if (newFailureCount >= MAX_CONSECUTIVE_FAILURES) {
        handleNetworkError();
      }
      // Otherwise, continue polling on next interval
    }
  }, [
    processLogCode,
    pollingStartedAt,
    consecutiveFailures,
    dispatch,
    handleCompleted,
    handleDeadLetter,
    handleTimeout,
    handleNetworkError,
  ]);

  // Start/stop polling based on pollingStatus
  useEffect(() => {
    const shouldPoll =
      pollingStatus === "QUEUED" ||
      pollingStatus === "PROCESSING" ||
      pollingStatus === "FAILED";

    if (shouldPoll && processLogCode && !intervalRef.current) {
      intervalRef.current = setInterval(poll, POLLING_INTERVAL_MS);
    }

    if (!shouldPoll) {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [pollingStatus, processLogCode, poll, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
      dispatch(reconciliationPollingReset());
    };
  }, []);

  // Retry function for network error or timeout
  const retry = useCallback(() => {
    dispatch(reconciliationPollingFailureReset());
    if (processLogCode) {
      dispatch(reconciliationPollingStatusUpdated("QUEUED"));
    }
  }, [dispatch, processLogCode]);

  return {
    pollingStatus,
    isPolling: (["QUEUED", "PROCESSING", "FAILED"] as ReconciliationJobStatus[]).includes(
      pollingStatus
    ),
    isSubmitting: pollingStatus === "SUBMITTING",
    retry,
    progress,
  };
};
